import React, {useEffect, useState} from "react";
import {Location, Session, User} from "../../types/Types";
import {LocationTag, LocationTagOption, ManagedTag} from "../../types/LocationTags";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faBed,
  faCheckCircle,
  faHammer,
  faGraduationCap,
  faHotel, faLocationDot,
  faPencilAlt,
  faPlus,
  faPlane, faStar,
  faUmbrellaBeach,
  faTimes,
  faTrain
} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";
import {getGoogleMapsLink, getCountryFlag} from "../../util/Util";
import {addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, getDocs, query, updateDoc, where} from "@firebase/firestore";
import {db} from "../../../firebase/FirebaseProvider";
import LikeButton from "../../tag/likes/LikeButton";
import {Button, Modal} from "react-bootstrap";
import LocationStayButton from "./LocationStayButton";

interface Props {
  location: Location | undefined
  currentUser: User | undefined
  likesMap: Map<string, boolean>
  onLikeCountChange?: (commentId: string, adjustment: number) => void;
  visited?: boolean;
  onToggleVisited?: () => void;
}

function formatAddress(address: string | undefined): string {
  if (!address) {
    return "";
  }

  const seenParts = new Set<string>();
  return address
      .split(",")
      .map(part => part.trim())
      .filter(part => {
        const normalisedPart = part.toLowerCase();
        if (!part || seenParts.has(normalisedPart)) {
          return false;
        }

        seenParts.add(normalisedPart);
        return true;
      })
      .join(", ");
}

function LocationDetails(props: Props) {
    const [showTagModal, setShowTagModal] = useState<boolean>(false);
    const [savingTagId, setSavingTagId] = useState<string | undefined>(undefined);
    const [locationTags, setLocationTags] = useState<Array<LocationTag>>([]);
    const [locationTagValues, setLocationTagValues] = useState<Array<string>>([]);
    const [locationTagOptions, setLocationTagOptions] = useState<Array<LocationTagOption>>([]);
    const [isLoadingLocationTagOptions, setIsLoadingLocationTagOptions] = useState<boolean>(true);
    const getTagCreatedAtTime = (createdAt: any): number => {
      if(!createdAt) {
        return Number.MAX_SAFE_INTEGER;
      }

      if(typeof createdAt.toDate === "function") {
        return createdAt.toDate().getTime();
      }

      return new Date(createdAt).getTime();
    };
    const getLocationTagOption = (tagValue: string): LocationTagOption | undefined => {
      return locationTagOptions.find((tagOption) => (
        tagOption.id === tagValue
      ));
    };

    const tagStatsById = locationTags.reduce((tagsMap, tag) => {
        const existingTag = tagsMap.get(tag.tagId);
        if(existingTag) {
          existingTag.userIds.add(tag.userId);
          existingTag.createdAtTime = Math.min(existingTag.createdAtTime, getTagCreatedAtTime(tag.createdAt));
        } else {
          tagsMap.set(tag.tagId, {
            tagId: tag.tagId,
            tagName: tag.tagName,
            userIds: new Set<string>([tag.userId]),
            createdAtTime: getTagCreatedAtTime(tag.createdAt),
          });
        }

        return tagsMap;
      }, new Map<string, {tagId: string, tagName: string, userIds: Set<string>, createdAtTime: number}>());
    const displayedLocationTags = Array.from(new Set(locationTagValues)).flatMap((tagValue, index) => {
      const tagOption = getLocationTagOption(tagValue);
      if(!tagOption) {
        return [];
      }

      const tagId = tagOption?.id || tagValue;
      const tagStats = tagStatsById.get(tagId);
      return {
        tagId,
        tagName: tagOption.name,
        userIds: tagStats?.userIds || new Set<string>(),
        createdAtTime: tagStats?.createdAtTime || Number.MAX_SAFE_INTEGER + index,
      };
    }).sort((tagA, tagB) => {
      const countDifference = tagB.userIds.size - tagA.userIds.size;
      if(countDifference !== 0) {
        return countDifference;
      }

      return tagA.createdAtTime - tagB.createdAtTime;
    });

    useEffect(() => {
      setLocationTagValues(props.location?.tags || []);
    }, [props.location]);

    useEffect(() => {
      (async () => {
        if(props.currentUser && props.currentUser.countUpdateEnabled) {
          await setCounts();
        }
      })();
    }, [props.currentUser, props.location]);

    useEffect(() => {
      (async () => {
        await loadLocationTags();
      })();
    }, [props.location]);

    useEffect(() => {
      (async () => {
        await loadLocationTagOptions();
      })();
    }, []);

    useEffect(() => {
      if(props.location?.pubIsClosed) {
        setShowTagModal(false);
      }
    }, [props.location]);

    const setCounts = async() => {
      if(!props.location) {
        return;
      }
      const visitsQuery = query(
          collection(db, 'visits'),
          where('locationId', '==', props.location.id),
          where('visited', "==", true)
      );
      const visitsSnapshot = await getDocs(visitsQuery);

      let sessions: Array<Session> = [];
      const sessionOnceCollection = query(
          collection(db, 'sessions'),
          where('locationId', '==', props.location.id)
      );
      const sessionsSnapshot = await getDocs(sessionOnceCollection);
      sessionsSnapshot.forEach((doc) => {
        let session = doc.data() as Session;
        sessions.push(session);
      });
      const uniqueSessions = new Set(sessions.map(session => session.locationId + "-" + session.userId));

      console.log(`Location name: ${props.location.name} - Values to update: [${visitsSnapshot.size}] visits - [${sessionsSnapshot.size}] sessions - [${uniqueSessions.size}] unique`)

      try {
        const locationRef = doc(db, 'locations', props.location.id);
        const data = {
          sessionCount: sessionsSnapshot.size,
          sessionUniqueCount: uniqueSessions.size,
          visitCount: visitsSnapshot.size,
        };
        await updateDoc(locationRef, data);
      } catch (error) {
        console.error("Error: ", error);
      }
    };

    const loadLocationTags = async() => {
      if(!props.location) {
        setLocationTags([]);
        return;
      }

      const locationTagsQuery = query(
          collection(db, 'location-tags'),
          where('locationId', '==', props.location.id)
      );
      const locationTagsSnapshot = await getDocs(locationTagsQuery);

      let tags: Array<LocationTag> = [];
      locationTagsSnapshot.forEach((doc) => {
        const tag = doc.data() as LocationTag;
        tags.push({
          ...tag,
          id: doc.id,
        });
      });
      setLocationTags(tags);
    };

    const loadLocationTagOptions = async() => {
      setIsLoadingLocationTagOptions(true);
      try {
        const locationTagOptionsQuery = query(
          collection(db, 'tags'),
          where('type', '==', 'LOCATION')
        );
        const locationTagOptionsSnapshot = await getDocs(locationTagOptionsQuery);
        const loadedTagOptions: Array<LocationTagOption> = [];

        locationTagOptionsSnapshot.forEach((tagDoc) => {
          const tag = tagDoc.data() as ManagedTag;
          if(tag.status === 'ACTIVE') {
            loadedTagOptions.push({
              id: tagDoc.id,
              name: tag.name,
              description: tag.description,
            });
          }
        });

        setLocationTagOptions(
          loadedTagOptions.sort((tagA, tagB) => tagA.name.localeCompare(tagB.name))
        );
      } catch (error) {
        console.error("Error loading location tag options: ", error);
      } finally {
        setIsLoadingLocationTagOptions(false);
      }
    };

    const toggleLocationTag = async(tag: LocationTagOption) => {
      if(!props.location || props.location.pubIsClosed || !props.currentUser || savingTagId) {
        return;
      }

      setSavingTagId(tag.id);
      try {
        const currentUserTags = locationTags.filter((locationTag) => (
          locationTag.tagId === tag.id && locationTag.userId === props.currentUser!.id
        ));

        if(currentUserTags.length > 0) {
          await Promise.all(
            currentUserTags
              .filter((locationTag) => !!locationTag.id)
              .map((locationTag) => deleteDoc(doc(db, 'location-tags', locationTag.id!)))
          );

          const remainingTags = locationTags.filter((locationTag) => (
            !(locationTag.tagId === tag.id && locationTag.userId === props.currentUser!.id)
          ));
          const hasRemainingTag = remainingTags.some((locationTag) => (
            locationTag.tagId === tag.id
          ));

          if(!hasRemainingTag) {
            await updateDoc(doc(db, 'locations', props.location.id), {
              tags: arrayRemove(tag.id),
              lastModified: new Date(),
            });
            setLocationTagValues((existingTagValues) => (
              existingTagValues.filter((tagValue) => tagValue !== tag.id)
            ));
          }

          setLocationTags(remainingTags);
          return;
        }

        const locationTag: LocationTag = {
          tagId: tag.id,
          tagName: tag.name,
          userId: props.currentUser.id,
          userName: props.currentUser.name,
          locationId: props.location.id,
          createdAt: new Date(),
        };
        const locationTagRef = await addDoc(collection(db, 'location-tags'), locationTag);
        await updateDoc(doc(db, 'locations', props.location.id), {
          tags: arrayUnion(tag.id),
          lastModified: new Date(),
        });
        setLocationTagValues((existingTagValues) => (
          existingTagValues.includes(tag.id) ? existingTagValues : [...existingTagValues, tag.id]
        ));
        setLocationTags((existingTags) => [
          ...existingTags,
          {
            ...locationTag,
            id: locationTagRef.id,
          },
        ]);
        setShowTagModal(false);
      } catch (error) {
        console.error("Error adding location tag: ", error);
      } finally {
        setSavingTagId(undefined);
      }
    };

    return (
        <>
          {props.currentUser && <div className={"location-header"}>
            {props.location && <div>
                {props.currentUser.admin && (
                  <Link className="location-header-edit-link" to={`/admin/${props.location.id}`} aria-label="Edit pub details">
                    <FontAwesomeIcon className="animated zoomIn icon-pen cursor-pointer" icon={faPencilAlt} />
                  </Link>
                )}
                <div>
                  <div className="d-inline-block">
                    <span>{props.location.name}</span>
                    <div className={"d-inline-block ml-1"} style={{ fontSize: "0.7em", marginTop: "7px", position: "absolute" }}>
                      {props.location.airport && <small className="text-truncate">
                        <FontAwesomeIcon className="ml-1 animated zoomIn icon-plane" icon={faPlane} />
                      </small>}
                      {props.location.hotel && <small className="text-truncate">
                        <FontAwesomeIcon className="ml-1 animated zoomIn icon-hotel" icon={faHotel} />
                      </small>}
                      {props.location.holidayPark && <small className="text-truncate">
                        <FontAwesomeIcon className="ml-1 animated zoomIn icon-holidaypark" icon={faUmbrellaBeach} />
                      </small>}
                      {props.location.isUniversity && <small className="text-truncate">
                        <FontAwesomeIcon className="ml-1 animated zoomIn icon-university" icon={faGraduationCap} />
                      </small>}
                      {props.location.trainStation && <small className="text-truncate">
                        <FontAwesomeIcon className="ml-1 animated zoomIn icon-train" icon={faTrain} />
                      </small>}
                      {props.location.pubIsClosed && <small className="text-truncate">
                        <FontAwesomeIcon className="ml-1 animated zoomIn icon-closed" icon={faTimes} />
                      </small>}
                      {(props.location.pubIsTemporaryClosed && !props.location.pubIsClosed) && <small className="text-truncate">
                        <FontAwesomeIcon className="ml-1 animated zoomIn icon-temporary" icon={faHammer} />
                      </small>}
                      {(props.location.bonus) && <small className="text-truncate">
                        <FontAwesomeIcon className="ml-1 animated zoomIn icon-bonus" icon={faStar} />
                      </small>}
                    </div>
                  </div>
                </div>
                {props.location.openingDate && (
                  <div style={{ fontSize: "0.4em", textAlign: "center", marginTop: "-0.3em" }}>
                    EST. {new Date(props.location.openingDate).getFullYear()}
	                    {props.location.pubIsClosed && (
	                      <span> - {props.location.closingDate ? new Date(props.location.closingDate).getFullYear() : "?"}</span>
	                    )}
	                  </div>
	                )}
	                <div style={{fontSize: "0.5em"}}>
	                <div className="d-inline-block">
	                  <span style={{ marginRight: "0.3em" }}>{getCountryFlag(props.location.country || '')}</span>{formatAddress(props.location.address)}
	                  <a className={"ml-1"}
                      style={{color: "white"}}
                      href={getGoogleMapsLink(props.location.name + ', ' + props.location.city)}
                      target={"_blank"} rel="noreferrer"
                  >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </a>
                </div>
                </div>
                <div style={{fontSize: "0.5em"}}>
                    <div>
                        <span><b>{props.location.visitCount}</b> visits</span>
                        {props.location.hotel && (
                          <span className="ml-1">- <b>{props.location.stayCount || 0}</b> <FontAwesomeIcon icon={faBed} title="Stays" /></span>
                        )}
                        {(props.location && props.location.sessionUniqueCount > 0) &&
                        <div className={"d-inline-block ml-1"}>
                          <span>- <b>{props.location.sessionUniqueCount}</b> <FontAwesomeIcon icon={faLocationDot} /></span>
                          {props.onToggleVisited && (
                            <div className="d-inline-block ml-1">
                              <div className="d-inline-block mr-1">-</div>
                              <FontAwesomeIcon 
                                className="cursor-pointer"
                                icon={faCheckCircle}
                                style={{ 
                                  color: props.visited ? 'white' : 'rgba(172, 172, 172, 0.48)',
                                  fontSize: '1em'
                                }}
                                onClick={props.onToggleVisited}
                              />
                            </div>
                          )}
                          {props.location.hotel && props.currentUser && (
                            <div className="d-inline-block ml-1">
                              <div className="d-inline-block mr-1">-</div>
                              <LocationStayButton location={props.location} currentUser={props.currentUser} variant="legacy" />
                            </div>
                          )}
                          <div className="d-inline-block ml-1">
                            <div className="d-inline-block mr-1">-</div>
                            <LikeButton
                              entityId={props.location.id}
                              likeType={"LOCATION"}
                              initialLikeCount={props.location.likeCount || 0}
                              isLiked={props.likesMap.get(props.location.id) || false}
                              currentUser={props.currentUser}
                              locationId={props.location.id}
                              locationName={props.location.name}
                              onLikeCountChange={props.onLikeCountChange}
                              unlikedColour="rgba(172, 172, 172, 0.48)"
                            />
                          </div>
                        </div>}
                    </div>
                </div>
                <div className={`location-tags-line ${!props.location.pubIsClosed ? "location-tags-line-open" : ""}`}>
                  {displayedLocationTags.map((tag) => (
                    <span className="location-display-tag" key={tag.tagId}>
                      {tag.tagName}
                      {tag.userIds.size > 1 && (
                        <span className="location-display-tag-count">{tag.userIds.size}</span>
                      )}
                    </span>
                  ))}
                  {!props.location.pubIsClosed && (
                    <button
                      className="location-add-tag-button"
                      type="button"
                      onClick={() => setShowTagModal(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} /> tag
                    </button>
                  )}
                </div>
              </div>}
            {!props.location && <div>...</div>}
            </div>}
            <Modal
              show={showTagModal && !props.location?.pubIsClosed}
              onHide={() => setShowTagModal(false)}
              dialogClassName="location-tag-modal"
              centered
            >
              <Modal.Header className="location-tag-modal-header">
                <Modal.Title>Add tag</Modal.Title>
                <button
                  className="location-tag-modal-close"
                  type="button"
                  aria-label="Close"
                  onClick={() => setShowTagModal(false)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </Modal.Header>
              <Modal.Body>
                <div className="location-tag-options">
                  {locationTagOptions.map((tag) => (
                    (() => {
                      const userHasTag = locationTags.some((locationTag) => (
                        locationTag.tagId === tag.id && locationTag.userId === props.currentUser?.id
                      ));

                      return (
                        <div key={tag.id} className="location-tag-option">
                          <Button
                            className="location-tag-option-button"
                            variant={userHasTag ? "secondary" : "outline-secondary"}
                            disabled={!!savingTagId}
                            onClick={() => toggleLocationTag(tag)}
                          >
                            {savingTagId === tag.id ? (userHasTag ? "Removing..." : "Saving...") : tag.name}
                          </Button>
                          <span className="location-tag-option-description">{tag.description}</span>
                        </div>
                      );
                    })()
                  ))}
                  {!isLoadingLocationTagOptions && locationTagOptions.length === 0 && (
                    <div className="text-center text-muted">No tags available</div>
                  )}
                  {isLoadingLocationTagOptions && (
                    <div className="text-center text-muted">Loading tags...</div>
                  )}
                </div>
              </Modal.Body>
            </Modal>
        </>
    );
}

export default LocationDetails;
